import { Button, Flex, Tooltip } from '@mantine/core'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'

import { useAnalytics } from '@flecto/analytics'
import { type FlectoAnalyticsEvents } from '@flecto/analytics/src/events'
import { type GetAllBookingOptions } from '@flecto/api/saas'
import { useUserVerified } from '@flecto/auth'
import { useQueryGetAllBookings } from '@flecto/core/saas'
import { useTranslation } from '@flecto/translations'
import { type UNSAFE_any, type CoreBooking } from '@flecto/types'
import { type DataTableSortStatus, Filters } from '@flecto/ui'
import { type FilterValue } from '@flecto/ui/@shared/Filters/components/multi-select/filterTypeConfig'
import { Page } from '@flecto/ui/saas'

import { FiltersBookings } from '../components/FiltersBookings/FiltersBookings'
import { TableBookings } from '../components/TableBookings/TableBookings'
import { useBookingFilterFields } from '../hooks/useBookingFilterFields'
import { useProcessBookingFilters } from '../hooks/useProcessBookingFilters'

export function PageBookingsList() {
  const router = useRouter()

  const { t } = useTranslation('saas', { keyPrefix: 'bookings' })

  const pageSize = 10
  const [currentPage, setCurrentPage] = useState(1)
  const [totalRecords, setTotalRecords] = useState(0)
  const [searchTerm, setSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState<GetAllBookingOptions['sort']>('id')
  const [orderBy, setOrderBy] = useState<GetAllBookingOptions['order']>('desc')
  const [bookings, setBookings] = useState<CoreBooking[]>([])
  const [filtersCustom, setFiltersCustom] = useState({})

  const { processBookingFilters } = useProcessBookingFilters()

  const { data, isLoading, isFetched } = useQueryGetAllBookings({
    q: searchTerm,
    limit: pageSize,
    offset: currentPage * pageSize - pageSize,
    sort: sortBy,
    order: orderBy,
    ...filtersCustom,
  })

  const bookingFilterFields = useBookingFilterFields()

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage)
  }

  const handleSearch = (term: string) => {
    setCurrentPage(1)
    setSearchTerm(term)
  }

  const track = useAnalytics('segment')

  const { t: generalTranslation } = useTranslation('saas', {
    keyPrefix: 'general',
  })

  const { isUserOnboarded, isUserVerified } = useUserVerified()

  const isDisabled = !isUserOnboarded || !isUserVerified

  const handleNewBooking = () => {
    track('BOOKING__NEW', {})
    router.push('/bookings/create')
  }

  const handleFilterChanges = (filtersApplied: FilterValue[]) => {
    const filters = filtersApplied ? processBookingFilters(filtersApplied) : {}
    setFiltersCustom(filters)
    setCurrentPage(1)
  }

  const handleTrackEvent = (
    eventName: keyof FlectoAnalyticsEvents,
    data: Record<string, UNSAFE_any>
  ) => {
    track(eventName, data)
  }

  const handleSortChanges = (sort: DataTableSortStatus<CoreBooking>) => {
    setSortBy(sort.columnAccessor as GetAllBookingOptions['sort'])
    setOrderBy(sort.direction)
  }

  useEffect(() => {
    if (isFetched) {
      setTotalRecords(data?.total_count ?? 0)
      setBookings(data?.booking ?? [])
    }
  }, [data, isFetched])

  return (
    <Page>
      <Page.Title>{t('page_title')}</Page.Title>
      <Page.Actions>
        <Tooltip
          withinPortal
          disabled={!isDisabled}
          position='bottom'
          label={generalTranslation(
            'shared.layout.notification_user_need_vefify'
          )}
        >
          <Button
            variant='filled'
            disabled={isDisabled}
            onClick={() => handleNewBooking()}
          >
            {t('label_new_booking')}
          </Button>
        </Tooltip>
      </Page.Actions>
      <Page.Content>
        <Flex direction='column' gap='md'>
          <Flex justify='flex-end' gap='md'>
            <Filters
              id='bookings-filters'
              isLoading={isLoading}
              fields={bookingFilterFields}
              onFilter={handleFilterChanges}
              onTrackEvent={handleTrackEvent}
            />

            <FiltersBookings onFilter={handleSearch} />
          </Flex>

          <TableBookings
            bookings={bookings}
            isLoading={isLoading}
            onRowClick={({ record: row }) =>
              router.push(`/bookings/${row.internal_id}`)
            }
            onSortStatusChange={handleSortChanges}
            sortStatus={{
              columnAccessor: sortBy ?? '',
              direction: orderBy ?? 'desc',
            }}
            withPaginationControlled={{
              currentPage,
              totalRecords,
              onPageChange: handlePageChange,
            }}
          />
        </Flex>
      </Page.Content>
    </Page>
  )
}
